import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createGlobalStyle } from 'styled-components';

import { Button } from '@desygner/ui-common-components';
import { useTranslation } from '@desygner/ui-common-translation';
import { TYPES as AURELIA_TYPES, IAureliaLoader } from '@desygner/ui-core-aurelia-loader';
import { useProjectsStore } from '@desygner/ui-features-projects';

import { ICredentialsManager } from '../../../../../../../core/viewer/credentials-manager';
import { TYPES } from '../../../../../../../inversify.types';
import { IViewerStoreInjector } from '../../../../../../../store';
import { useConfigStore } from '../../../../../../../store/config.store';
import { useResponsive } from '../../../../../../hooks/useResponsive';
import { AureliaShareModalService } from './AureliaShareModalService';

const GlobalAureliaModalStyle = createGlobalStyle`
  ux-dialog-container {
    font-family: ${({ theme }) => theme.Typography.FontFamily};
  }
`;

const DEFAULT_TEMPLATE_SHARE_OPTIONS = ['teamshared'];

export interface AureliaShareModalProps {
  onRequestApproval: () => void;
  isApprovalRequired: boolean;
}

export function AureliaShareModal({
  onRequestApproval,
  isApprovalRequired,
}: AureliaShareModalProps) {
  const { t } = useTranslation();
  const { isTabletOrMobile } = useResponsive();
  const { project: projectStore } = useProjectsStore();
  const { setProjectVisibility } = projectStore;
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);

  const projectViewer = useViewerStore((state) => state.project);

  const aureliaLoader = useInjection<IAureliaLoader>(AURELIA_TYPES.AureliaLoader);

  const credentialsManager = useInjection<ICredentialsManager>(TYPES.Viewer.CredentialsManager);

  const shareModalServiceRef = useRef<AureliaShareModalService | null>(null);
  const [isAureliaLoaded, setIsAureliaLoaded] = useState(false);

  useEffect(
    function initializeAureliaShareModal() {
      if (shareModalServiceRef.current) {
        return;
      }

      const configState = useConfigStore.getState();
      const viewerState = useViewerStore.getState();
      const company = viewerState.company;

      const whiteLabelConfiguration =
        company && 'white_label_configuration' in company
          ? company.white_label_configuration
          : undefined;

      shareModalServiceRef.current =
        shareModalServiceRef.current ??
        new AureliaShareModalService(aureliaLoader, credentialsManager);

      shareModalServiceRef.current
        .initialize({
          ...configState,
          whiteLabelConfiguration,
        })
        .then(() => {
          setIsAureliaLoaded(true);
        })
        .catch((error) => {
          console.error('Error initializing Aurelia share modal:', error);
        });
    },
    [aureliaLoader, credentialsManager, useViewerStore]
  );

  // Listen for privacy updates from Aurelia share modal and update React store
  useEffect(
    function subscribeToPrivacyUpdates() {
      const handlePrivacyUpdate = (event: Event) => {
        const customEvent = event as CustomEvent<{
          designId: number;
          is_public: boolean;
          is_team_shared: boolean;
          is_private: boolean;
        }>;

        const { is_public, is_team_shared } = customEvent.detail;

        setProjectVisibility({ is_public, is_team_shared });
      };

      document.addEventListener('share:privacy-updated', handlePrivacyUpdate);

      return () => {
        document.removeEventListener('share:privacy-updated', handlePrivacyUpdate);
      };
    },
    [setProjectVisibility]
  );

  const handleOpenShareModal = useCallback(async () => {
    if (!shareModalServiceRef.current?.isReady()) {
      console.error('Aurelia share modal service not ready');
      return;
    }

    if (!projectStore.currentProject) {
      console.error('Project data not available');
      return;
    }

    if (isApprovalRequired) {
      onRequestApproval();
      return;
    }

    const design = projectStore.currentProject;
    const previewImage = design.pages?.[0]?.thumbnail || '';
    const isTemplate = projectViewer.type === 'template';

    const aureliaDesign = {
      ...design,
      encoded_id: design.encoded_id,
      is_public: design.is_public,
      is_private: !design.is_public,
      is_team_shared: (design as any).is_team_shared,
      is_template: isTemplate,
      pages:
        design.pages?.map((page) => ({
          ...page,
          thumbnail: page.thumbnail,
        })) || [],
    };

    const linkOptionTypes = isTemplate ? DEFAULT_TEMPLATE_SHARE_OPTIONS : [];

    shareModalServiceRef.current.openShareModal({
      design: aureliaDesign,
      previewImage,
      plan: null,
      linkOptionTypes,
      closeCallback: () => {
        document.dispatchEvent(new CustomEvent('share:modal.closed'));
      },
    });
  }, [projectStore.currentProject, projectViewer.type, isApprovalRequired, onRequestApproval]);

  return (
    <>
      <GlobalAureliaModalStyle />
      <Button
        action="share"
        title={isTabletOrMobile ? undefined : t('tool.topbar.share_tooltip')}
        variant="ghost"
        size="Small"
        icon={
          !isTabletOrMobile
            ? {
                name: 'share---triangle',
                size: 'Small',
              }
            : undefined
        }
        onClick={handleOpenShareModal}
        label={isTabletOrMobile ? t('tool.topbar.share_button') : undefined}
        loading={!isAureliaLoaded}
        disabled={!isAureliaLoaded}
      />
    </>
  );
}
